import { useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Download, LayoutGrid, Plus, Printer, RefreshCw, Trash2 } from "lucide-react";
import { Badge, Button, Card, CardDescription, CardHeader, CardTitle, ConfirmDialog, EmptyState, Field, Input, Switch, toast } from "@dishfolio/ui";
import { api, withOrg } from "../lib/api.ts";
import { useOrg } from "../lib/org.tsx";
import { useRestaurants, useTables } from "../lib/queries.ts";
import { PageContainer, PageHeader } from "../components/Page.tsx";
import { notifyError, UpgradeCard } from "../components/Upgrade.tsx";

export function TablesPage() {
  const { restaurantId } = useParams({ from: "/app/restaurants/$restaurantId/tables" });
  const { org, can, feature } = useOrg();
  const restaurants = useRestaurants();
  const tables = useTables(restaurantId);
  const qc = useQueryClient();
  const [label, setLabel] = useState("");
  const [from, setFrom] = useState(1);
  const [to, setTo] = useState(10);
  const [rotate, setRotate] = useState<string | null>(null);
  const r = restaurants.data?.find((x) => x.id === restaurantId);
  const refresh = () => qc.invalidateQueries({ queryKey: ["tables", restaurantId] });
  if (org && !org.plan.tableOrdering) {
    return (
      <PageContainer>
        <PageHeader title="Tables" />
        <UpgradeCard plan="Business" title="Table QR codes" description="Each table gets its own QR code so orders arrive tagged with the right table." />
      </PageContainer>
    );
  }
  const manage = can("tables:manage");
  return (
    <PageContainer wide>
      <PageHeader
        eyebrow={r?.name}
        title="Tables & QR codes"
        description="A table QR identifies the table for ordering — it never grants staff access. Rotate a code if it’s been copied or misused."
        actions={
          <>
            {feature("table_management") ? (
              <Button asChild variant="outline">
                <Link to="/restaurants/$restaurantId/floor" params={{ restaurantId }}>
                  <LayoutGrid /> Floor plan
                </Link>
              </Button>
            ) : null}
            <Button variant="outline" onClick={() => window.print()}>
              <Printer /> Print all
            </Button>
          </>
        }
      />
      {manage ? (
        <Card className="mb-6 gap-4 print:hidden">
          <CardHeader>
            <CardTitle>Add tables</CardTitle>
            <CardDescription>
              Up to {org?.plan.maxTables} tables per location. {r?.orderingEnabled ? "" : "Turn on table ordering in the location settings to accept orders."}
            </CardDescription>
          </CardHeader>
          <div className="flex flex-wrap items-end gap-3">
            <form
              className="flex items-end gap-2"
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await api.post(`/restaurants/${restaurantId}/tables`, { label });
                  setLabel("");
                  await refresh();
                } catch (err) {
                  notifyError(err);
                }
              }}
            >
              <Field label="Single table">
                <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Terrace 3" />
              </Field>
              <Button type="submit" variant="outline" disabled={!label.trim()}>
                <Plus /> Add
              </Button>
            </form>
            <span className="pb-2 text-sm text-muted-foreground">or</span>
            <div className="flex items-end gap-2">
              <Field label="From">
                <Input type="number" className="w-20" value={from} onChange={(e) => setFrom(Number(e.target.value))} />
              </Field>
              <Field label="To">
                <Input type="number" className="w-20" value={to} onChange={(e) => setTo(Number(e.target.value))} />
              </Field>
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const res = await api.post<{ created: number }>(`/restaurants/${restaurantId}/tables/bulk`, { prefix: "Table ", from, to });
                    toast.success(`Created ${res.created} tables`);
                    await refresh();
                  } catch (err) {
                    notifyError(err);
                  }
                }}
              >
                Add range
              </Button>
            </div>
          </div>
        </Card>
      ) : null}
      {tables.data?.length === 0 ? (
        <EmptyState title="No tables yet" description="Add your tables to print a QR code for each one." />
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 print:grid-cols-3">
          {tables.data?.map((t) => (
            <Card key={t.id} className="items-center gap-2 p-4 text-center break-inside-avoid">
              <div className="flex w-full items-center justify-between">
                <span className="font-semibold">{t.label}</span>
                {!t.isActive ? <Badge variant="outline">Off</Badge> : null}
              </div>
              <img src={withOrg(`/api/v1/restaurants/${restaurantId}/tables/${t.id}/qr?format=svg&size=320`)} alt={`QR code for ${t.label}`} className="aspect-square w-full max-w-[180px]" />
              <div className="text-[11px] text-muted-foreground">Scan to see the menu and order</div>
              {manage ? (
                <div className="flex w-full items-center justify-between gap-1 print:hidden">
                  <Switch
                    checked={t.isActive}
                    aria-label="Active"
                    onCheckedChange={async (v) => {
                      await api.patch(`/restaurants/${restaurantId}/tables/${t.id}`, { isActive: v });
                      await refresh();
                    }}
                  />
                  <div className="flex">
                    <Button variant="ghost" size="icon-sm" asChild aria-label="Download PNG">
                      <a href={withOrg(`/api/v1/restaurants/${restaurantId}/tables/${t.id}/qr?format=png&size=1024&download=1`)}>
                        <Download />
                      </a>
                    </Button>
                    <Button variant="ghost" size="icon-sm" onClick={() => setRotate(t.id)} aria-label="Rotate code">
                      <RefreshCw />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Delete table"
                      onClick={async () => {
                        await api.del(`/restaurants/${restaurantId}/tables/${t.id}`);
                        await refresh();
                      }}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                </div>
              ) : null}
            </Card>
          ))}
        </div>
      )}
      <ConfirmDialog
        open={Boolean(rotate)}
        onOpenChange={(o) => !o && setRotate(null)}
        title="Replace this table’s QR code?"
        description="The old printed code stops working immediately. Print and place the new one."
        confirmLabel="Replace code"
        onConfirm={async () => {
          await api.post(`/restaurants/${restaurantId}/tables/${rotate}/rotate`);
          await refresh();
        }}
      />
    </PageContainer>
  );
}
